import { type FC, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { Relationship } from "@/shared/constants/relationshipOptions";
import { RelationshipSelect } from "@/pages/onboarding/components/RelationshipSelect";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { useOnboardingAuth } from "@/shared/hooks/useOnboardingAuth";
import writeIllustration from "@/shared/assets/illustrations/write.svg";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { saveEstateDetailsSafe } from "@/shared/utils/saveEstateDetails";
import { validateDecedentDates } from "@/shared/utils/validators";
import { buildFullName } from "@/shared/utils/names";
import { EventType, setPersonProperties, trackEvent } from "@/shared/utils/core";
import { getOnboardingFlow } from "@/shared/utils/funnelAnalytics";

export const DecedentContactDetails: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  onComplete,
  goToScreen,
}) => {
  return (
    <ManualPathForm
      answers={answers}
      setAnswer={setAnswer}
      onComplete={onComplete}
      goToScreen={goToScreen}
    />
  );
};

const ManualPathForm: FC<
  Pick<FlowCustomComponentProps, "answers" | "setAnswer" | "onComplete" | "goToScreen">
> = ({ answers, setAnswer, onComplete, goToScreen }) => {
  const [firstName, setFirstName] = useState((answers["decedent-first-name"] as string) || "");
  const [middleName, setMiddleName] = useState((answers["decedent-middle-name"] as string) || "");
  const [lastName, setLastName] = useState((answers["decedent-last-name"] as string) || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{ message: string; field: string } | null>(null);

  const { createPersonRecord } = useOnboardingAuth();

  const relationship = (answers["decedent-relationship"] as string) || "";
  const displayName = firstName || "them";

  const fieldError = (field: string) =>
    error?.field === field ? "border-destructive border-[2px]" : "";

  const handleSubmit = async () => {
    if (isLoading) return;

    if (!firstName.trim()) {
      setError({ message: "First name is required", field: "decedent-first-name" });
      return;
    }
    if (!lastName.trim()) {
      setError({ message: "Last name is required", field: "decedent-last-name" });
      return;
    }
    if (!relationship) {
      setError({
        message: `Choose your relationship to ${displayName}`,
        field: "decedent-relationship",
      });
      return;
    }

    const dob = (answers["decedent-dob"] as string) || undefined;
    const dod = (answers["decedent-dod"] as string) || undefined;

    const dateValidation = validateDecedentDates(dob, dod, "decedent-dob", "decedent-dod");
    if (dateValidation) {
      setError(dateValidation);
      return;
    }

    const trimmedMiddle = middleName.trim();
    const fullName = buildFullName(firstName.trim(), trimmedMiddle, lastName.trim());
    setAnswer("decedent-name", fullName);
    setAnswer("scraped-middle-name", trimmedMiddle);

    if (relationship === Relationship.Attorney) {
      goToScreen("attorney-redirect");
      return;
    }

    setIsLoading(true);
    setError(null);

    const personRecord = await createPersonRecord({
      fullName,
      middleName: trimmedMiddle || undefined,
    });

    if (!personRecord) {
      setIsLoading(false);
      setError({ message: "Your details weren't saved. Try again.", field: "" });
      return;
    }

    await saveEstateDetailsSafe({ estateId: personRecord.estate_id, dob, dod, relationship });

    trackEvent(EventType.DECEDENT_DETAILS_SAVED, {
      flow: getOnboardingFlow(answers),
      estate_id: String(personRecord.estate_id),
      decedent_path: "manual",
      relationship,
      fields_edited: "",
      edited_field_count: 0,
      has_dob: Boolean(dob),
      has_dod: Boolean(dod),
    });
    setPersonProperties({}, { decedent_path: "manual" });

    setIsLoading(false);
    onComplete();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <img src={writeIllustration} alt="Enter details" className="h-24 w-24" />
        <h2>Tell us about the person who passed</h2>
        <p className="text-secondary">Answer once, and you won't be asked twice</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="decedent-first-name">First name</Label>
          <Input
            id="decedent-first-name"
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setAnswer("decedent-first-name", e.target.value);
              if (error?.field === "decedent-first-name") setError(null);
            }}
            className={fieldError("decedent-first-name")}
          />
          {error?.field === "decedent-first-name" && (
            <FieldError message={error.message} className="mt-1" />
          )}
        </div>
        <div>
          <Label htmlFor="decedent-middle-name">
            Middle name <span className="font-normal text-tertiary">(optional)</span>
          </Label>
          <Input
            id="decedent-middle-name"
            type="text"
            placeholder="Michael"
            value={middleName}
            onChange={(e) => {
              setMiddleName(e.target.value);
              setAnswer("decedent-middle-name", e.target.value);
            }}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="decedent-last-name">Last name</Label>
        <Input
          id="decedent-last-name"
          type="text"
          placeholder="Doe"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            setAnswer("decedent-last-name", e.target.value);
            if (error?.field === "decedent-last-name") setError(null);
          }}
          className={fieldError("decedent-last-name")}
        />
        {error?.field === "decedent-last-name" && (
          <FieldError message={error.message} className="mt-1" />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="decedent-dob">Date of birth</Label>
          <DatePicker
            id="decedent-dob"
            dateString={(answers["decedent-dob"] as string) || undefined}
            onChange={() => {}}
            onDateStringChange={(val) => {
              setAnswer("decedent-dob", val || "");
              if (error?.field === "decedent-dob") setError(null);
            }}
            placeholder="Pick a date"
            className={fieldError("decedent-dob")}
          />
          {error?.field === "decedent-dob" && (
            <FieldError message={error.message} className="mt-1" />
          )}
        </div>
        <div>
          <Label htmlFor="decedent-dod">Date of passing</Label>
          <DatePicker
            id="decedent-dod"
            dateString={(answers["decedent-dod"] as string) || undefined}
            onChange={() => {}}
            onDateStringChange={(val) => {
              setAnswer("decedent-dod", val || "");
              if (error?.field === "decedent-dod") setError(null);
            }}
            placeholder="Pick a date"
            className={fieldError("decedent-dod")}
          />
          {error?.field === "decedent-dod" && (
            <FieldError message={error.message} className="mt-1" />
          )}
        </div>
      </div>

      <RelationshipSelect
        name={firstName}
        value={relationship}
        onChange={(value) => {
          setAnswer("decedent-relationship", value);
          if (error?.field === "decedent-relationship") setError(null);
        }}
        error={error?.field === "decedent-relationship" ? error.message : undefined}
      />

      {error?.field === "" && <FieldError message={error.message} />}

      <div className="mt-4">
        <Button
          variant="brand"
          rounded={true}
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              {relationship === Relationship.Attorney
                ? "Create an attorney account"
                : "See my roadmap"}{" "}
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
